import { Box, Button, Center, Field, Flex, IconButton, Input, NumberInput, Span, Text, VStack } from "@chakra-ui/react";
import { DndContext, type DragEndEvent, PointerSensor, useSensor } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LuInfo, LuPlus, LuTrash2 } from "react-icons/lu";
import { MdDragIndicator } from "react-icons/md";
import { useShopContext } from "../shop";
import { OptionalTag } from "./ItemForm/OptionalTag";
import { Tooltip } from "./ui/tooltip";

type Variant = { id: string; name: string; stockQuantity: string | number; sku: string };

type VariantsFormProps = {
  variants: Variant[];
  onChange: (variants: Variant[]) => void;
};

function VariantForm({
  index,
  variant,
  onChange,
  onDelete,
}: {
  index: number;
  variant: Variant;
  onChange: (variant: Variant) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: variant.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "none",
  };

  return (
    <Flex
      w="100%"
      justify="space-between"
      align="center"
      gap={1}
      key={variant.id}
      ref={setNodeRef}
      style={style}
      cursor="auto"
      {...attributes}
    >
      <Flex justify="center" align="center" gap={1} w={8} {...listeners} cursor="grab">
        <MdDragIndicator />
        <Text>{index + 1}.</Text>
      </Flex>
      <Field.Root flex={1}>
        <Input
          borderColor={"gray.300"}
          placeholder="Sサイズ"
          px={2}
          value={variant.name}
          onChange={(e) =>
            onChange({
              ...variant,
              name: e.target.value,
            })
          }
        />
      </Field.Root>
      <Field.Root flex={1}>
        <Input
          borderColor={"gray.300"}
          placeholder="SKU-123"
          px={2}
          value={variant.sku}
          onChange={(e) =>
            onChange({
              ...variant,
              sku: e.target.value,
            })
          }
        />
      </Field.Root>
      <Field.Root w={12}>
        <NumberInput.Root
          min={0}
          value={variant.stockQuantity.toString()}
          onValueChange={(details) =>
            onChange({
              ...variant,
              stockQuantity: details.valueAsNumber || 0,
            })
          }
        >
          <NumberInput.Input px={2} borderColor={"gray.300"} />
        </NumberInput.Root>
      </Field.Root>
      <IconButton aria-label="Remove" onClick={onDelete} rounded="md" variant="subtle">
        <LuTrash2 />
      </IconButton>
    </Flex>
  );
}

export function VariantsForm({ variants, onChange }: VariantsFormProps) {
  const { currentShopId } = useShopContext();
  const sensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const handleVariantChange = (index: number, variant: Variant) => {
    const newVariants = [...variants];
    newVariants[index] = variant;
    onChange(newVariants);
  };

  return (
    <>
      {variants.length === 1 ? (
        <VStack w="100%" gap={2}>
          <Flex w="100%" justify="space-between" align="center" gap={1}>
            <Text fontSize="sm" flex={1}>
              名前
              <OptionalTag ml={2} />
              <Span display="inline-block" width="18px" height="18px" ml={2} pt={1}>
                <Tooltip
                  content={
                    <Text>
                      文字数上限についての注意事項
                      <br />
                      ・楽天市場: 全角10文字まで
                    </Text>
                  }
                >
                  <span>
                    <LuInfo />
                  </span>
                </Tooltip>{" "}
              </Span>
            </Text>
            <Text fontSize="sm" flex={1}>
              SKU/商品コード
              <OptionalTag ml={2} />
            </Text>
            <Text fontSize="sm" w={12}>
              在庫数
            </Text>
          </Flex>
          <Flex w="100%" justify="space-between" align="center" gap={1}>
            <Field.Root flex={1}>
              <Input
                borderColor={"gray.300"}
                placeholder="Sサイズ"
                px={2}
                value={variants[0].name}
                onChange={(e) =>
                  handleVariantChange(0, {
                    ...variants[0],
                    name: e.target.value,
                  })
                }
              />
            </Field.Root>
            <Field.Root flex={1}>
              <Input
                borderColor={"gray.300"}
                placeholder="SKU-123"
                value={variants[0].sku}
                onChange={(e) =>
                  handleVariantChange(0, {
                    ...variants[0],
                    sku: e.target.value,
                  })
                }
              />
            </Field.Root>
            <Field.Root w={12}>
              <NumberInput.Root
                min={0}
                value={variants[0].stockQuantity.toString()}
                onValueChange={(details) =>
                  handleVariantChange(0, {
                    ...variants[0],
                    stockQuantity: details.valueAsNumber || 0,
                  })
                }
              >
                <NumberInput.Input px={2} borderColor={"gray.300"} />
              </NumberInput.Root>
            </Field.Root>
          </Flex>
        </VStack>
      ) : (
        <DndContext
          sensors={[sensor]}
          onDragEnd={(event: DragEndEvent) => {
            const { active, over } = event;

            if (active && over && active.id !== over.id) {
              const oldIndex = variants.findIndex((v) => v.id === active.id);
              const newIndex = variants.findIndex((v) => v.id === over.id);
              onChange(arrayMove(variants, oldIndex, newIndex));
            }
          }}
        >
          <VStack w="100%" gap={2}>
            <Flex w="100%" justify="space-between" align="center" gap={1}>
              <Box w={8} />
              <Text fontSize="sm" flex={1}>
                名前
                <Span display="inline-block" width="18px" height="18px" ml={2} pt={1}>
                  <Tooltip
                    content={
                      <Text>
                        文字数上限についての注意事項
                        <br />
                        ・楽天市場: 全角10文字まで
                      </Text>
                    }
                  >
                    <span>
                      <LuInfo />
                    </span>
                  </Tooltip>{" "}
                </Span>
              </Text>
              <Text fontSize="sm" flex={1}>
                SKU/商品コード
                <OptionalTag ml={2} />
              </Text>
              <Text fontSize="sm" w={12}>
                在庫数
              </Text>
              <Box w={10} />
            </Flex>
            <SortableContext items={variants.map((v) => v.id)} strategy={verticalListSortingStrategy}>
              {variants.map((variant, index) => (
                <VariantForm
                  index={index}
                  key={variant.id}
                  variant={variant}
                  onChange={(variant) => handleVariantChange(index, variant)}
                  onDelete={() => {
                    const newVariants = [...variants];
                    newVariants.splice(index, 1);
                    if (newVariants.length === 1) {
                      newVariants[0].name = "";
                    }
                    onChange(newVariants);
                  }}
                />
              ))}
            </SortableContext>
          </VStack>
        </DndContext>
      )}
      <Center w="100%">
        <Button
          size="sm"
          variant="subtle"
          mt={2}
          aria-label="Add"
          onClick={() => {
            const newVariants = [...variants, { id: `${Date.now()}`, name: "", stockQuantity: 1, sku: "" }];
            onChange(newVariants);
          }}
        >
          <LuPlus />
          {`${currentShopId === "d4df73ce-0788-4756-9685-e36acb3954c8" ? "サイズ" : "種類"}を追加する`}
        </Button>
      </Center>
    </>
  );
}
